import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { ClipboardList, Search, Filter, Download, RefreshCw, Calendar, User, Database, ChevronDown, ChevronUp, AlertCircle, Eye, Clock } from 'lucide-react';

const EVENT_TYPES = [
  { value: 'all', label: 'Tous les événements' },
  { value: 'INSERT', label: 'Création' },
  { value: 'UPDATE', label: 'Modification' },
  { value: 'DELETE', label: 'Suppression' },
  { value: 'LOGIN', label: 'Connexion' },
  { value: 'LOGOUT', label: 'Déconnexion' },
  { value: 'EXPORT', label: 'Export' },
  { value: 'ARCHIVE', label: 'Archivage' },
];

const TABLE_SOURCES = [
  { value: 'all', label: 'Toutes les tables' },
  { value: 'user_profiles', label: 'Profils Utilisateurs' },
  { value: 'properties', label: 'Propriétés' },
  { value: 'appointments', label: 'Rendez-vous' },
  { value: 'property_inquiries', label: 'Demandes' },
  { value: 'notifications', label: 'Notifications' },
  { value: 'frontend_content', label: 'Contenu' },
  { value: 'professional_applications', label: 'Candidatures' },
  { value: 'professionals', label: 'Professionnels' },
];

const DATE_RANGES = [
  { value: 'all', label: 'Toute la période' },
  { value: 'today', label: "Aujourd\'hui" },
  { value: '7days', label: '7 derniers jours' },
  { value: '30days', label: '30 derniers jours' },
  { value: '90days', label: '90 derniers jours' },
];

const ACTION_COLORS = {
  INSERT: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  DELETE: 'bg-red-100 text-red-800',
  LOGIN: 'bg-purple-100 text-purple-800',
  LOGOUT: 'bg-gray-100 text-gray-700',
  EXPORT: 'bg-amber-100 text-amber-800',
  ARCHIVE: 'bg-orange-100 text-orange-800',
};

const AuditLogsPanel = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    event_type: 'all',
    table_source: 'all',
    date_range: '30days',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const buildDateFilter = (range) => {
    const now = new Date();
    switch (range) {
      case 'today':
        return new Date(now.getFullYear(), now.getMonth(), now.getDate())?.toISOString();
      case '7days':
        return new Date(now.getTime() - 7 * 86400000)?.toISOString();
      case '30days':
        return new Date(now.getTime() - 30 * 86400000)?.toISOString();
      case '90days':
        return new Date(now.getTime() - 90 * 86400000)?.toISOString();
      default:
        return null;
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        ?.from('activity_logs')
        ?.select(`*, user:user_profiles(full_name, email, role)`, { count: 'exact' })
        ?.order('created_at', { ascending: false })
        ?.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (filters?.event_type !== 'all') {
        query = query?.eq('action', filters?.event_type);
      }
      if (filters?.table_source !== 'all') {
        query = query?.eq('entity_type', filters?.table_source);
      }
      const dateFrom = buildDateFilter(filters?.date_range);
      if (dateFrom) {
        query = query?.gte('created_at', dateFrom);
      }
      if (searchTerm?.trim()) {
        query = query?.or(`entity_type.ilike.%${searchTerm}%,action.ilike.%${searchTerm}%`);
      }

      const { data, error: fetchError, count } = await query;
      if (fetchError) throw fetchError;
      setLogs(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      setError('Erreur lors du chargement des journaux: ' + err?.message);
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExportCSV = () => {
    if (!logs?.length) return;
    const headers = ['Date', 'Action', 'Table', 'Utilisateur', 'Email', 'ID Entité'];
    const rows = logs?.map(log => [
      formatDate(log?.created_at),
      log?.action || '',
      log?.entity_type || '',
      log?.user?.full_name || 'Système',
      log?.user?.email || '',
      log?.entity_id || '',
    ]);
    const csv = [headers, ...rows]?.map(r => r?.map(v => `"${String(v)?.replace(/"/g, '""')}"`)?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const colorClass = ACTION_COLORS?.[action] || 'bg-gray-100 text-gray-700';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
        {action}
      </span>
    );
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Journaux d'Audit</h3>
            <p className="text-sm text-gray-500">
              {totalCount > 0 ? `${totalCount?.toLocaleString('fr-FR')} entrées` : 'Toutes les transactions de la plateforme'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchLogs}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actualiser"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleExportCSV}
            disabled={!logs?.length}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>
      {/* Search + Filter Bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => { setSearchTerm(e?.target?.value); setPage(0); }}
              placeholder="Rechercher par table, action..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            <Filter className="w-4 h-4" />
            Filtres
            {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type d'Événement</label>
              <select
                value={filters?.event_type}
                onChange={e => { setFilters(f => ({ ...f, event_type: e?.target?.value })); setPage(0); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {EVENT_TYPES?.map(opt => <option key={opt?.value} value={opt?.value}>{opt?.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Source (Table)</label>
              <select
                value={filters?.table_source}
                onChange={e => { setFilters(f => ({ ...f, table_source: e?.target?.value })); setPage(0); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {TABLE_SOURCES?.map(opt => <option key={opt?.value} value={opt?.value}>{opt?.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Période</label>
              <select
                value={filters?.date_range}
                onChange={e => { setFilters(f => ({ ...f, date_range: e?.target?.value })); setPage(0); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {DATE_RANGES?.map(opt => <option key={opt?.value} value={opt?.value}>{opt?.label}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>
      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {/* Logs Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(8)]?.map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-36" />
                <div className="h-5 bg-gray-100 rounded w-16" />
                <div className="h-4 bg-gray-200 rounded w-28" />
                <div className="h-4 bg-gray-100 rounded w-32" />
                <div className="h-4 bg-gray-200 rounded w-24" />
              </div>
            ))}
          </div>
        ) : logs?.length === 0 ? (
          <div className="p-10 text-center">
            <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Aucun journal trouvé</p>
            <p className="text-gray-400 text-sm mt-1">Modifiez vos filtres pour voir plus de résultats.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Date</span>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Action</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      <span className="flex items-center gap-1"><Database className="w-3 h-3" /> Table</span>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> Utilisateur</span>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">ID Entité</th>
                    <th className="px-4 py-3 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs?.map(log => (
                    <React.Fragment key={log?.id}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(log?.created_at)}
                          </span>
                        </td>
                        <td className="px-4 py-3">{getActionBadge(log?.action)}</td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-800">{log?.entity_type || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          {log?.user ? (
                            <div>
                              <p className="font-medium text-gray-900 text-xs">{log?.user?.full_name}</p>
                              <p className="text-gray-400 text-xs">{log?.user?.email}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">Système</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs font-mono truncate max-w-[120px]">
                          {log?.entity_id ? log?.entity_id?.substring(0, 12) + '...' : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {(log?.old_data || log?.new_data) && (
                            <button
                              onClick={() => setExpandedId(expandedId === log?.id ? null : log?.id)}
                              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Voir les détails"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                      {expandedId === log?.id && (
                        <tr className="bg-blue-50">
                          <td colSpan={6} className="px-4 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {log?.old_data && (
                                <div>
                                  <p className="text-xs font-semibold text-red-600 mb-1">Avant (old_data)</p>
                                  <pre className="text-xs bg-red-50 border border-red-200 rounded p-2 overflow-auto max-h-32 text-gray-700">
                                    {JSON.stringify(log?.old_data, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {log?.new_data && (
                                <div>
                                  <p className="text-xs font-semibold text-green-600 mb-1">Après (new_data)</p>
                                  <pre className="text-xs bg-green-50 border border-green-200 rounded p-2 overflow-auto max-h-32 text-gray-700">
                                    {JSON.stringify(log?.new_data, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-xs text-gray-500">
                  Page {page + 1} sur {totalPages} ({totalCount?.toLocaleString('fr-FR')} entrées)
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-white disabled:opacity-40 transition-colors"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-white disabled:opacity-40 transition-colors"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogsPanel;
